import { HTMLAttributes } from "react";

export interface JcCommonProps extends Pick<HTMLAttributes<HTMLElement>, "className" | "style" | "id"> {
    /** Test hook forwarded to the root DOM node. Prefer an accessible name; use this only when role/text can't target the element. */
    readonly "data-testid"?: string;
}

export enum JcSize {
    EXTRA_SMALL = "extra-small",
    SMALL = "small",
    MEDIUM = "medium",
    LARGE = "large",
    EXTRA_LARGE = "extra-large",
    XX_LARGE = "xx-large",
    /** Apply no preset dimensions — size comes from className/CSS or the container. */
    NONE = "none",
}

export type JcEmptyProps = {}

export type OneOf<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<infer ElementType> ? ElementType : never;

export type JcKeyFunction<T> = (item: T) => string;

export type JcFilterFunction<T> = (item: T, filterString: string) => boolean;

/** String-valued members of an enum object, ignoring any non-string properties from a merged namespace (e.g. `fromDTO`, `label` helpers). */
type EnumStringValues<T> = Extract<T[keyof T], string>;

export class JcUtils {


    public static commonProps(props: JcCommonProps, additionalClassNames?: (string | false | null | undefined)[]): JcCommonProps {
        const { className, style, id } = props;
        return {
            className: JcUtils.classNameString((additionalClassNames ?? []).concat(className)),
            style,
            id,
            "data-testid": props["data-testid"],
        };
    }

    public static otherProps<T extends JcCommonProps>(props: T): Omit<T, "className" | "style" | "id" | "data-testid"> {
        const { className, style, id, "data-testid": _testId, ...otherProps } = props;

        return otherProps;
    }

    public static classNameString(classNames: (string | false | null | undefined)[]): string {
        const retNames: string[] = [];
        classNames.forEach(s => {
            if (s) retNames.push(s);
        })
        return retNames.join(" ");
    }

    public static isNotNull<T>(value: T | null): value is T {
        return value != null;
    }

    public static lastOrNull<T>(list: T[]): T | null {
        if (list.length === 0) {
            return null;
        }
        return list[list.length - 1];
    }

    public static firstOrNull<T>(list: T[]): T | null {
        if (list.length === 0) {
            return null;
        }
        return list[0];
    }

    public static randomOf<T>(values: readonly T[]): T {
        return values[Math.floor(Math.random() * values.length)];
    }

    /**
     * Validates that a string is a member of an enum, including enums with a merged
     * namespace of helper functions (e.g. `fromDTO`, `label`).
     * @param enumObject The enum object to validate against
     * @param value The string value to validate
     * @returns The matching enum value or undefined if no match found
     */
    public static validateEnumValue<T extends object>(
        enumObject: T,
        value: string | undefined
    ): EnumStringValues<T> | undefined {
        if (value === undefined) {
            return undefined;
        }
        return Object.values(enumObject).find(
            (enumValue): enumValue is EnumStringValues<T> => typeof enumValue === "string" && enumValue === value
        );
    }

}
